/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Tessera Backend API
 * Tessera backend application with Bearer token authentication and multi-tenant support
 */
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  InvalidateOptions,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import type { BodyType, ErrorType } from '../api-instance';
import { apiInstance } from '../api-instance';
import type {
  BulkUpdateDashboardPermissionsPathParameters,
  CreateDashboardPermissionPathParameters,
  DeleteDashboardPermissionPathParameters,
  GetDashboardPermissionsPathParameters,
  TLDashboardPermissionCreateRequest,
  TLDashboardPermissionResponse,
  TLDashboardPermissionsBulkUpdateRequest,
  TLDashboardPermissionsResponse,
  TLDashboardPermissionUpdateRequest,
  TLHTTPValidationError,
  TLSuccessResponse,
  UpdateDashboardPermissionPathParameters,
} from './tessera-backend-api.schemas';

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * @summary Get My Dashboard Permissions
 */
export const getMyDashboardPermissions = (options?: SecondParameter<typeof apiInstance>, signal?: AbortSignal) => {
  return apiInstance<TLDashboardPermissionsResponse>(
    { url: `/v2/data-harmonization/dashboard-permissions`, method: 'GET', signal },
    options,
  );
};

export const getGetMyDashboardPermissionsQueryKey = () => {
  return ['v2', 'data-harmonization', 'dashboard-permissions'] as const;
};

export const getGetMyDashboardPermissionsQueryOptions = <
  TData = Awaited<ReturnType<typeof getMyDashboardPermissions>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getMyDashboardPermissions>>, TError, TData>>;
  request?: SecondParameter<typeof apiInstance>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetMyDashboardPermissionsQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getMyDashboardPermissions>>> = ({ signal }) =>
    getMyDashboardPermissions(requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getMyDashboardPermissions>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetMyDashboardPermissionsQueryResult = NonNullable<Awaited<ReturnType<typeof getMyDashboardPermissions>>>;
export type GetMyDashboardPermissionsQueryError = ErrorType<unknown>;

export function useGetMyDashboardPermissions<
  TData = Awaited<ReturnType<typeof getMyDashboardPermissions>>,
  TError = ErrorType<unknown>,
>(
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getMyDashboardPermissions>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getMyDashboardPermissions>>,
          TError,
          Awaited<ReturnType<typeof getMyDashboardPermissions>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetMyDashboardPermissions<
  TData = Awaited<ReturnType<typeof getMyDashboardPermissions>>,
  TError = ErrorType<unknown>,
>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getMyDashboardPermissions>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getMyDashboardPermissions>>,
          TError,
          Awaited<ReturnType<typeof getMyDashboardPermissions>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetMyDashboardPermissions<
  TData = Awaited<ReturnType<typeof getMyDashboardPermissions>>,
  TError = ErrorType<unknown>,
>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getMyDashboardPermissions>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get My Dashboard Permissions
 */

export function useGetMyDashboardPermissions<
  TData = Awaited<ReturnType<typeof getMyDashboardPermissions>>,
  TError = ErrorType<unknown>,
>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getMyDashboardPermissions>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetMyDashboardPermissionsQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get My Dashboard Permissions
 */
export const prefetchGetMyDashboardPermissionsQuery = async <
  TData = Awaited<ReturnType<typeof getMyDashboardPermissions>>,
  TError = ErrorType<unknown>,
>(
  queryClient: QueryClient,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getMyDashboardPermissions>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetMyDashboardPermissionsQueryOptions(options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get My Dashboard Permissions
 */
export const invalidateGetMyDashboardPermissions = async (
  queryClient: QueryClient,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetMyDashboardPermissionsQueryKey() }, options);

  return queryClient;
};

/**
 * @summary Get Dashboard Permissions
 */
export const getDashboardPermissions = (
  { dashboardId }: GetDashboardPermissionsPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLDashboardPermissionsResponse>(
    { url: `/v2/data-harmonization/dashboard-permissions/${dashboardId}`, method: 'GET', signal },
    options,
  );
};

export const getGetDashboardPermissionsQueryKey = ({ dashboardId }: GetDashboardPermissionsPathParameters) => {
  return ['v2', 'data-harmonization', 'dashboard-permissions', dashboardId] as const;
};

export const getGetDashboardPermissionsQueryOptions = <
  TData = Awaited<ReturnType<typeof getDashboardPermissions>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { dashboardId }: GetDashboardPermissionsPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDashboardPermissions>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetDashboardPermissionsQueryKey({ dashboardId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getDashboardPermissions>>> = ({ signal }) =>
    getDashboardPermissions({ dashboardId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: dashboardId !== null && dashboardId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getDashboardPermissions>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetDashboardPermissionsQueryResult = NonNullable<Awaited<ReturnType<typeof getDashboardPermissions>>>;
export type GetDashboardPermissionsQueryError = ErrorType<TLHTTPValidationError>;

export function useGetDashboardPermissions<
  TData = Awaited<ReturnType<typeof getDashboardPermissions>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetDashboardPermissionsPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDashboardPermissions>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getDashboardPermissions>>,
          TError,
          Awaited<ReturnType<typeof getDashboardPermissions>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetDashboardPermissions<
  TData = Awaited<ReturnType<typeof getDashboardPermissions>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetDashboardPermissionsPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDashboardPermissions>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getDashboardPermissions>>,
          TError,
          Awaited<ReturnType<typeof getDashboardPermissions>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetDashboardPermissions<
  TData = Awaited<ReturnType<typeof getDashboardPermissions>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetDashboardPermissionsPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDashboardPermissions>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Dashboard Permissions
 */

export function useGetDashboardPermissions<
  TData = Awaited<ReturnType<typeof getDashboardPermissions>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { dashboardId }: GetDashboardPermissionsPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDashboardPermissions>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetDashboardPermissionsQueryOptions({ dashboardId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Dashboard Permissions
 */
export const prefetchGetDashboardPermissionsQuery = async <
  TData = Awaited<ReturnType<typeof getDashboardPermissions>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { dashboardId }: GetDashboardPermissionsPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDashboardPermissions>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetDashboardPermissionsQueryOptions({ dashboardId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Dashboard Permissions
 */
export const invalidateGetDashboardPermissions = async (
  queryClient: QueryClient,
  { dashboardId }: GetDashboardPermissionsPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetDashboardPermissionsQueryKey({ dashboardId }) }, options);

  return queryClient;
};

/**
 * @summary Create Dashboard Permission
 */
export const createDashboardPermission = (
  { dashboardId }: CreateDashboardPermissionPathParameters,
  tLDashboardPermissionCreateRequest: BodyType<TLDashboardPermissionCreateRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLDashboardPermissionsResponse>(
    {
      url: `/v2/data-harmonization/dashboard-permissions/${dashboardId}`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLDashboardPermissionCreateRequest,
      signal,
    },
    options,
  );
};

export const getCreateDashboardPermissionQueryKey = (
  { dashboardId }: CreateDashboardPermissionPathParameters,
  tLDashboardPermissionCreateRequest?: BodyType<TLDashboardPermissionCreateRequest>,
) => {
  return [
    'POST',
    'v2',
    'data-harmonization',
    'dashboard-permissions',
    dashboardId,
    tLDashboardPermissionCreateRequest,
  ] as const;
};

export const getCreateDashboardPermissionQueryOptions = <
  TData = Awaited<ReturnType<typeof createDashboardPermission>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { dashboardId }: CreateDashboardPermissionPathParameters,
  tLDashboardPermissionCreateRequest: BodyType<TLDashboardPermissionCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createDashboardPermission>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getCreateDashboardPermissionQueryKey({ dashboardId }, tLDashboardPermissionCreateRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof createDashboardPermission>>> = ({ signal }) =>
    createDashboardPermission({ dashboardId }, tLDashboardPermissionCreateRequest, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: dashboardId !== null && dashboardId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof createDashboardPermission>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type CreateDashboardPermissionQueryResult = NonNullable<Awaited<ReturnType<typeof createDashboardPermission>>>;
export type CreateDashboardPermissionQueryError = ErrorType<TLHTTPValidationError>;

export function useCreateDashboardPermission<
  TData = Awaited<ReturnType<typeof createDashboardPermission>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: CreateDashboardPermissionPathParameters,
  tLDashboardPermissionCreateRequest: BodyType<TLDashboardPermissionCreateRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof createDashboardPermission>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof createDashboardPermission>>,
          TError,
          Awaited<ReturnType<typeof createDashboardPermission>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCreateDashboardPermission<
  TData = Awaited<ReturnType<typeof createDashboardPermission>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: CreateDashboardPermissionPathParameters,
  tLDashboardPermissionCreateRequest: BodyType<TLDashboardPermissionCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createDashboardPermission>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof createDashboardPermission>>,
          TError,
          Awaited<ReturnType<typeof createDashboardPermission>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCreateDashboardPermission<
  TData = Awaited<ReturnType<typeof createDashboardPermission>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: CreateDashboardPermissionPathParameters,
  tLDashboardPermissionCreateRequest: BodyType<TLDashboardPermissionCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createDashboardPermission>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Create Dashboard Permission
 */

export function useCreateDashboardPermission<
  TData = Awaited<ReturnType<typeof createDashboardPermission>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { dashboardId }: CreateDashboardPermissionPathParameters,
  tLDashboardPermissionCreateRequest: BodyType<TLDashboardPermissionCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createDashboardPermission>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getCreateDashboardPermissionQueryOptions(
    { dashboardId },
    tLDashboardPermissionCreateRequest,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Create Dashboard Permission
 */
export const prefetchCreateDashboardPermissionQuery = async <
  TData = Awaited<ReturnType<typeof createDashboardPermission>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { dashboardId }: CreateDashboardPermissionPathParameters,
  tLDashboardPermissionCreateRequest: BodyType<TLDashboardPermissionCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createDashboardPermission>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getCreateDashboardPermissionQueryOptions(
    { dashboardId },
    tLDashboardPermissionCreateRequest,
    options,
  );

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Create Dashboard Permission
 */
export const invalidateCreateDashboardPermission = async (
  queryClient: QueryClient,
  { dashboardId }: CreateDashboardPermissionPathParameters,
  tLDashboardPermissionCreateRequest: BodyType<TLDashboardPermissionCreateRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getCreateDashboardPermissionQueryKey({ dashboardId }, tLDashboardPermissionCreateRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Bulk Update Dashboard Permissions
 */
export const bulkUpdateDashboardPermissions = (
  { dashboardId }: BulkUpdateDashboardPermissionsPathParameters,
  tLDashboardPermissionsBulkUpdateRequest: BodyType<TLDashboardPermissionsBulkUpdateRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLDashboardPermissionsResponse>(
    {
      url: `/v2/data-harmonization/dashboard-permissions/${dashboardId}`,
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      data: tLDashboardPermissionsBulkUpdateRequest,
      signal,
    },
    options,
  );
};

export const getBulkUpdateDashboardPermissionsQueryKey = (
  { dashboardId }: BulkUpdateDashboardPermissionsPathParameters,
  tLDashboardPermissionsBulkUpdateRequest?: BodyType<TLDashboardPermissionsBulkUpdateRequest>,
) => {
  return [
    'PUT',
    'v2',
    'data-harmonization',
    'dashboard-permissions',
    dashboardId,
    tLDashboardPermissionsBulkUpdateRequest,
  ] as const;
};

export const getBulkUpdateDashboardPermissionsQueryOptions = <
  TData = Awaited<ReturnType<typeof bulkUpdateDashboardPermissions>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { dashboardId }: BulkUpdateDashboardPermissionsPathParameters,
  tLDashboardPermissionsBulkUpdateRequest: BodyType<TLDashboardPermissionsBulkUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof bulkUpdateDashboardPermissions>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getBulkUpdateDashboardPermissionsQueryKey({ dashboardId }, tLDashboardPermissionsBulkUpdateRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof bulkUpdateDashboardPermissions>>> = ({ signal }) =>
    bulkUpdateDashboardPermissions({ dashboardId }, tLDashboardPermissionsBulkUpdateRequest, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: dashboardId !== null && dashboardId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof bulkUpdateDashboardPermissions>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type BulkUpdateDashboardPermissionsQueryResult = NonNullable<
  Awaited<ReturnType<typeof bulkUpdateDashboardPermissions>>
>;
export type BulkUpdateDashboardPermissionsQueryError = ErrorType<TLHTTPValidationError>;

export function useBulkUpdateDashboardPermissions<
  TData = Awaited<ReturnType<typeof bulkUpdateDashboardPermissions>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: BulkUpdateDashboardPermissionsPathParameters,
  tLDashboardPermissionsBulkUpdateRequest: BodyType<TLDashboardPermissionsBulkUpdateRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof bulkUpdateDashboardPermissions>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof bulkUpdateDashboardPermissions>>,
          TError,
          Awaited<ReturnType<typeof bulkUpdateDashboardPermissions>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useBulkUpdateDashboardPermissions<
  TData = Awaited<ReturnType<typeof bulkUpdateDashboardPermissions>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: BulkUpdateDashboardPermissionsPathParameters,
  tLDashboardPermissionsBulkUpdateRequest: BodyType<TLDashboardPermissionsBulkUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof bulkUpdateDashboardPermissions>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof bulkUpdateDashboardPermissions>>,
          TError,
          Awaited<ReturnType<typeof bulkUpdateDashboardPermissions>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useBulkUpdateDashboardPermissions<
  TData = Awaited<ReturnType<typeof bulkUpdateDashboardPermissions>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: BulkUpdateDashboardPermissionsPathParameters,
  tLDashboardPermissionsBulkUpdateRequest: BodyType<TLDashboardPermissionsBulkUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof bulkUpdateDashboardPermissions>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Bulk Update Dashboard Permissions
 */

export function useBulkUpdateDashboardPermissions<
  TData = Awaited<ReturnType<typeof bulkUpdateDashboardPermissions>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { dashboardId }: BulkUpdateDashboardPermissionsPathParameters,
  tLDashboardPermissionsBulkUpdateRequest: BodyType<TLDashboardPermissionsBulkUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof bulkUpdateDashboardPermissions>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getBulkUpdateDashboardPermissionsQueryOptions(
    { dashboardId },
    tLDashboardPermissionsBulkUpdateRequest,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Bulk Update Dashboard Permissions
 */
export const prefetchBulkUpdateDashboardPermissionsQuery = async <
  TData = Awaited<ReturnType<typeof bulkUpdateDashboardPermissions>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { dashboardId }: BulkUpdateDashboardPermissionsPathParameters,
  tLDashboardPermissionsBulkUpdateRequest: BodyType<TLDashboardPermissionsBulkUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof bulkUpdateDashboardPermissions>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getBulkUpdateDashboardPermissionsQueryOptions(
    { dashboardId },
    tLDashboardPermissionsBulkUpdateRequest,
    options,
  );

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Bulk Update Dashboard Permissions
 */
export const invalidateBulkUpdateDashboardPermissions = async (
  queryClient: QueryClient,
  { dashboardId }: BulkUpdateDashboardPermissionsPathParameters,
  tLDashboardPermissionsBulkUpdateRequest: BodyType<TLDashboardPermissionsBulkUpdateRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getBulkUpdateDashboardPermissionsQueryKey({ dashboardId }, tLDashboardPermissionsBulkUpdateRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Delete Dashboard Permission
 */
export const deleteDashboardPermission = (
  { dashboardId, permissionId }: DeleteDashboardPermissionPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLSuccessResponse>(
    { url: `/v2/data-harmonization/dashboard-permissions/${dashboardId}/${permissionId}`, method: 'DELETE', signal },
    options,
  );
};

export const getDeleteDashboardPermissionQueryKey = ({
  dashboardId,
  permissionId,
}: DeleteDashboardPermissionPathParameters) => {
  return ['DELETE', 'v2', 'data-harmonization', 'dashboard-permissions', dashboardId, permissionId] as const;
};

export const getDeleteDashboardPermissionQueryOptions = <
  TData = Awaited<ReturnType<typeof deleteDashboardPermission>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { dashboardId, permissionId }: DeleteDashboardPermissionPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteDashboardPermission>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getDeleteDashboardPermissionQueryKey({ dashboardId, permissionId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof deleteDashboardPermission>>> = ({ signal }) =>
    deleteDashboardPermission({ dashboardId, permissionId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: dashboardId !== null && dashboardId !== undefined && permissionId !== null && permissionId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof deleteDashboardPermission>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type DeleteDashboardPermissionQueryResult = NonNullable<Awaited<ReturnType<typeof deleteDashboardPermission>>>;
export type DeleteDashboardPermissionQueryError = ErrorType<TLHTTPValidationError>;

export function useDeleteDashboardPermission<
  TData = Awaited<ReturnType<typeof deleteDashboardPermission>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DeleteDashboardPermissionPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteDashboardPermission>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof deleteDashboardPermission>>,
          TError,
          Awaited<ReturnType<typeof deleteDashboardPermission>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useDeleteDashboardPermission<
  TData = Awaited<ReturnType<typeof deleteDashboardPermission>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DeleteDashboardPermissionPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteDashboardPermission>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof deleteDashboardPermission>>,
          TError,
          Awaited<ReturnType<typeof deleteDashboardPermission>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useDeleteDashboardPermission<
  TData = Awaited<ReturnType<typeof deleteDashboardPermission>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DeleteDashboardPermissionPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteDashboardPermission>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Delete Dashboard Permission
 */

export function useDeleteDashboardPermission<
  TData = Awaited<ReturnType<typeof deleteDashboardPermission>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { dashboardId, permissionId }: DeleteDashboardPermissionPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteDashboardPermission>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getDeleteDashboardPermissionQueryOptions({ dashboardId, permissionId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Delete Dashboard Permission
 */
export const prefetchDeleteDashboardPermissionQuery = async <
  TData = Awaited<ReturnType<typeof deleteDashboardPermission>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { dashboardId, permissionId }: DeleteDashboardPermissionPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteDashboardPermission>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getDeleteDashboardPermissionQueryOptions({ dashboardId, permissionId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Delete Dashboard Permission
 */
export const invalidateDeleteDashboardPermission = async (
  queryClient: QueryClient,
  { dashboardId, permissionId }: DeleteDashboardPermissionPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getDeleteDashboardPermissionQueryKey({ dashboardId, permissionId }) },
    options,
  );

  return queryClient;
};

/**
 * @summary Update Dashboard Permission
 */
export const updateDashboardPermission = (
  { dashboardId, permissionId }: UpdateDashboardPermissionPathParameters,
  tLDashboardPermissionUpdateRequest: BodyType<TLDashboardPermissionUpdateRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLDashboardPermissionResponse>(
    {
      url: `/v2/data-harmonization/dashboard-permissions/${dashboardId}/${permissionId}`,
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      data: tLDashboardPermissionUpdateRequest,
      signal,
    },
    options,
  );
};

export const getUpdateDashboardPermissionQueryKey = (
  { dashboardId, permissionId }: UpdateDashboardPermissionPathParameters,
  tLDashboardPermissionUpdateRequest?: BodyType<TLDashboardPermissionUpdateRequest>,
) => {
  return [
    'PUT',
    'v2',
    'data-harmonization',
    'dashboard-permissions',
    dashboardId,
    permissionId,
    tLDashboardPermissionUpdateRequest,
  ] as const;
};

export const getUpdateDashboardPermissionQueryOptions = <
  TData = Awaited<ReturnType<typeof updateDashboardPermission>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { dashboardId, permissionId }: UpdateDashboardPermissionPathParameters,
  tLDashboardPermissionUpdateRequest: BodyType<TLDashboardPermissionUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateDashboardPermission>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getUpdateDashboardPermissionQueryKey({ dashboardId, permissionId }, tLDashboardPermissionUpdateRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof updateDashboardPermission>>> = ({ signal }) =>
    updateDashboardPermission(
      { dashboardId, permissionId },
      tLDashboardPermissionUpdateRequest,
      requestOptions,
      signal,
    );

  return {
    queryKey,
    queryFn,
    enabled: dashboardId !== null && dashboardId !== undefined && permissionId !== null && permissionId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof updateDashboardPermission>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type UpdateDashboardPermissionQueryResult = NonNullable<Awaited<ReturnType<typeof updateDashboardPermission>>>;
export type UpdateDashboardPermissionQueryError = ErrorType<TLHTTPValidationError>;

export function useUpdateDashboardPermission<
  TData = Awaited<ReturnType<typeof updateDashboardPermission>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateDashboardPermissionPathParameters,
  tLDashboardPermissionUpdateRequest: BodyType<TLDashboardPermissionUpdateRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateDashboardPermission>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof updateDashboardPermission>>,
          TError,
          Awaited<ReturnType<typeof updateDashboardPermission>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUpdateDashboardPermission<
  TData = Awaited<ReturnType<typeof updateDashboardPermission>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateDashboardPermissionPathParameters,
  tLDashboardPermissionUpdateRequest: BodyType<TLDashboardPermissionUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateDashboardPermission>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof updateDashboardPermission>>,
          TError,
          Awaited<ReturnType<typeof updateDashboardPermission>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUpdateDashboardPermission<
  TData = Awaited<ReturnType<typeof updateDashboardPermission>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateDashboardPermissionPathParameters,
  tLDashboardPermissionUpdateRequest: BodyType<TLDashboardPermissionUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateDashboardPermission>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Update Dashboard Permission
 */

export function useUpdateDashboardPermission<
  TData = Awaited<ReturnType<typeof updateDashboardPermission>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { dashboardId, permissionId }: UpdateDashboardPermissionPathParameters,
  tLDashboardPermissionUpdateRequest: BodyType<TLDashboardPermissionUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateDashboardPermission>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getUpdateDashboardPermissionQueryOptions(
    { dashboardId, permissionId },
    tLDashboardPermissionUpdateRequest,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Update Dashboard Permission
 */
export const prefetchUpdateDashboardPermissionQuery = async <
  TData = Awaited<ReturnType<typeof updateDashboardPermission>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { dashboardId, permissionId }: UpdateDashboardPermissionPathParameters,
  tLDashboardPermissionUpdateRequest: BodyType<TLDashboardPermissionUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateDashboardPermission>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getUpdateDashboardPermissionQueryOptions(
    { dashboardId, permissionId },
    tLDashboardPermissionUpdateRequest,
    options,
  );

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Update Dashboard Permission
 */
export const invalidateUpdateDashboardPermission = async (
  queryClient: QueryClient,
  { dashboardId, permissionId }: UpdateDashboardPermissionPathParameters,
  tLDashboardPermissionUpdateRequest: BodyType<TLDashboardPermissionUpdateRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    {
      queryKey: getUpdateDashboardPermissionQueryKey({ dashboardId, permissionId }, tLDashboardPermissionUpdateRequest),
    },
    options,
  );

  return queryClient;
};
